import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import useMutation from '../../hooks/useMutation'
import UpdateOnlineSchedulePricing from '../../graphql/mutations/updateOnlineSchedulePricing'
import Modal from '../modal/Modal'
import styles from './index.module.scss'
import notify from '../../utilities/notify'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'

const DIALOGS = {
  disable: {
    title: 'Disable pricing?',
    body: 'Disabling pricing will hide pricing information on your Online Store.'
  },
  disableCardCapture: {
    title: 'Disable pricing?',
    body: 'Currently, card capture is enabled within your Online Store. We recommend disabling card capture prior to disabling pricing. Please contact Client Support:'
  },
  enable: {
    title: 'Enable Pricing?',
    body: (
      <>
        Enabling pricing will show pricing information on your Online Store. If you wish to enable card capture for future sales, please reach-out to{' '}
        <a href='mailto:sales@dispatcher.com'>sales@dispatcher.com</a>
      </>
    )
  }
}

export default function OnlineSchedulePricingToggle ({
  haulerId,
  pricingEnabled = true,
  enableCardCapture = false,
  cardCaptureLoading = false,
  pricingMessage = '',
  onPricingUpdated
}) {
  const [message, setMessage] = useState(pricingMessage)
  const [editorMode, setEditorMode] = useState(null)
  const [dialog, setDialog] = useState(null)
  const messageInputRef = useRef(null)

  useEffect(() => {
    if (editorMode) return
    setMessage(pricingMessage)
  }, [pricingMessage, editorMode])

  const showMessageEditor = editorMode != null

  useLayoutEffect(() => {
    const el = messageInputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [message, showMessageEditor])

  const { mutate: updatePricingSettings, isLoading: isUpdating } = useMutation(
    UpdateOnlineSchedulePricing,
    {
      async onSuccess ({ pricing }) {
        notify(
          'success',
          `Pricing ${pricing.pricingDisabled ? 'disabled' : 'enabled'} for Online Schedule`
        )
        setEditorMode(null)

        if (onPricingUpdated) {
          await onPricingUpdated()
        }
      },
      onError (error) {
        captureErrorAndNotify(error, 'Error updating Online Schedule pricing setting')
      }
    }
  )

  const showSavedExplanation = !pricingEnabled && editorMode !== 'edit'

  function handleToggleClick () {
    if (pricingEnabled) {
      setDialog(enableCardCapture ? 'disableCardCapture' : 'disable')
      return
    }
    setDialog('enable')
  }

  function closeDialog () {
    setDialog(null)
  }

  function handleConfirmDisable () {
    setDialog(null)
    setEditorMode('draft')
  }

  function handleConfirmEnable () {
    if (isUpdating) return
    setDialog(null)
    updatePricingSettings({
      haulerId,
      settings: {
        pricingDisabled: false,
        pricingMessage
      }
    })
  }

  function handleCancel () {
    setMessage(pricingMessage)
    setEditorMode(null)
  }

  function handleSave () {
    if (!message.trim()) {
      notify('error', 'Please add an explanation')
      return
    }

    updatePricingSettings({
      haulerId,
      settings: {
        pricingDisabled: true,
        pricingMessage: message
      }
    })
  }

  const dialogCopy = DIALOGS[dialog] || null

  return (
    <>
      <div className={cn('dis-panel', styles.panel)}>
        <button
          type='button'
          disabled={isUpdating || editorMode !== null || dialogCopy !== null || (pricingEnabled && cardCaptureLoading)}
          onClick={handleToggleClick}
          className={cn('dis-btn dis-btn-lg', {
            'dis-btn-primary': !pricingEnabled,
            'dis-btn-danger': pricingEnabled
          })}>
          {pricingEnabled ? 'Disable Pricing' : 'Enable Pricing'}
        </button>

        <p className={styles.note}>
          Note: Disabling pricing is a global setting and applies to all Products.
          Since customers will no longer see pricing at checkout, please include
          an explanation of how scheduling and payment work for your business.
        </p>

        {showMessageEditor && (
          <div className={styles.messageWrapper}>
            <div className={styles.explanationHeader}>
              <label htmlFor='pricingDisabledExplanation' className='control-label'>
                Explanation shown to customers
              </label>
            </div>
            <textarea
              id='pricingDisabledExplanation'
              ref={messageInputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              aria-describedby='pricingDisabledExplanationCount'
              className={cn('form-control', styles.messageInput)}
            />
            <div
              id='pricingDisabledExplanationCount'
              className={styles.charCount}>
              {message.length} / 1000
            </div>
            <div className={styles.actions}>
              <button
                type='button'
                disabled={isUpdating}
                onClick={handleCancel}
                className='dis-btn dis-btn-lg dis-btn-danger'>
                Cancel
              </button>
              <button
                type='button'
                disabled={isUpdating || !message.trim()}
                onClick={handleSave}
                className='dis-btn dis-btn-lg dis-btn-primary'>
                {editorMode === 'draft' ? 'Save & Disable Pricing' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {showSavedExplanation && (
          <div className={styles.messageWrapper}>
            <div className={styles.explanationHeader}>
              <span className='control-label'>
                Explanation shown to customers
              </span>
              <button
                type='button'
                className={cn('dis-list-group-header-link', styles.editLink)}
                onClick={() => setEditorMode('edit')}>
                Edit
              </button>
            </div>
            <p className={styles.savedMessage}>{pricingMessage}</p>
          </div>
        )}
      </div>
      <Modal isOpen={Boolean(dialogCopy)} closeModal={closeDialog}>
        {dialogCopy && (
          <div className={cn('dis-panel', styles.dialog)}>
            {dialogCopy.title && <h2 className={styles.dialogTitle}>{dialogCopy.title}</h2>}
            <p className={styles.dialogBody}>{dialogCopy.body}</p>
            {dialog === 'disableCardCapture' && (
              <p className={styles.dialogContact}>
                (855) 757-6400
                <br />
                <a href='mailto:support@dispatcher.com'>Support@dispatcher.com</a>
                <br />
                Chat with us using the chat feature in Dispatcher
              </p>
            )}
            <div className={styles.dialogActions}>
              {dialog === 'disableCardCapture'
                ? (
                  <button
                    type='button'
                    className='dis-btn dis-btn-lg dis-btn-danger'
                    onClick={closeDialog}>
                    Close
                  </button>
                  )
                : (
                  <>
                    <button
                      type='button'
                      className='dis-btn dis-btn-lg dis-btn-danger'
                      onClick={closeDialog}>
                      Cancel
                    </button>
                    <button
                      type='button'
                      disabled={isUpdating}
                      className='dis-btn dis-btn-lg dis-btn-primary'
                      onClick={dialog === 'enable' ? handleConfirmEnable : handleConfirmDisable}>
                      {dialog === 'enable' ? 'Yes, enable pricing' : 'Yes, disable pricing'}
                    </button>
                  </>
                  )}
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

OnlineSchedulePricingToggle.propTypes = {
  haulerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  pricingEnabled: PropTypes.bool,
  enableCardCapture: PropTypes.bool,
  cardCaptureLoading: PropTypes.bool,
  pricingMessage: PropTypes.string,
  onPricingUpdated: PropTypes.func
}
